import React, { Modal, Form, Button, Badge, Alert, Spinner } from 'react-bootstrap'; // Added Alert, Spinner
import { useState, useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import { updateGoals as dbUpdateGoals, updateRestrictions as dbUpdateRestrictions } from '../services/dbService'; // Adjust path as needed

const UserDashboardModal = ({
    show,
    onClose,
    userId,
    initialGoals = [],
    initialRestrictions = [],
    scans = [],
    onGoalsUpdate,
    onRestrictionsUpdate
}) => {
    const [currentGoals, setCurrentGoals] = useState([]);
    const [currentRestrictions, setCurrentRestrictions] = useState([]);

    const [goalInput, setGoalInput] = useState('');
    const [goalDeadline, setGoalDeadline] = useState('');
    const [showDeadline, setShowDeadline] = useState(false);
    const [restrictionInput, setRestrictionInput] = useState('');
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);

    useEffect(() => {
        if (show) { // Only update when modal becomes visible or initial props change
            setCurrentGoals([...initialGoals]); // Use spread to create new array instance
            setCurrentRestrictions([...initialRestrictions]);
            setError(''); // Clear previous errors when modal opens
            // Reset form fields
            setGoalInput('');
            setGoalDeadline('');
            setShowDeadline(false);
            setRestrictionInput('');
        }
    }, [initialGoals, initialRestrictions, show]);


    useEffect(() => {
        if (!show || !chartRef.current) {
            if (chartInstanceRef.current) { // Ensure chart is destroyed if modal is hidden
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
            return;
        }

        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }
        console.log("Rendering chart with scans:", scans); // Debugging line
        const safeScans = Array.isArray(scans) ? scans : [];

        console.log("Safe scans:", safeScans); // Debugging line
        const scansByDate = {};
        const now = new Date();



        for (let i = 13; i >= 0; i--) {
            const date = new Date(now);
            date.setDate(now.getDate() - i);
            const dateStr = date.toISOString().split('T')[0];
            scansByDate[dateStr] = 0;
        }

        safeScans.forEach(scan => {
            console.log("Processing scan:", scan); // Debugging line
            if (scan && scan.created_at) {
                try {
                    const localDate = new Date(scan.created_at);
                    if (!isNaN(localDate.getTime())) { // Check if date is valid
                        const dateStr = localDate.toISOString().split('T')[0];
                        if (scansByDate[dateStr] !== undefined) {
                            scansByDate[dateStr]++;
                        }
                    }
                } catch (e) {
                    console.warn("Invalid date in scan record:", scan.created_at);
                }
            }
        });

        const labels = Object.keys(scansByDate).sort();
        const data = labels.map(date => scansByDate[date]);
        const formattedLabels = labels.map(dateStr => {
            const d = new Date(dateStr); // Ensure we parse the date string
            return `${d.toLocaleString('default', { month: 'short' })} ${d.getDate()}`;
        });

        const ctx = chartRef.current.getContext('2d');
        chartInstanceRef.current = new Chart(ctx, {
            type: 'line',
            data: {
                labels: formattedLabels,
                datasets: [{
                    label: 'Scans',
                    data,
                    backgroundColor: 'rgba(79, 70, 229, 0.2)',
                    borderColor: 'rgba(79, 70, 229, 1)',
                    tension: 0.4,
                    fill: true,
                    pointBackgroundColor: 'rgba(79, 70, 229, 1)',
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false }, title: { display: true, text: 'Scan Activity (Last 14 Days)' } },
                scales: { y: { beginAtZero: true, ticks: { precision: 0 } }, x: { grid: { display: false } } }
            }
        });

        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };
    }, [show, scans]);

    const getGoalStatus = (deadline) => {
        if (!deadline) return 'normal';
        const today = new Date();
        const deadlineDate = new Date(deadline);
        if (isNaN(deadlineDate.getTime())) return 'normal'; // Invalid deadline
        const diffDays = (deadlineDate.setHours(0, 0, 0, 0) - today.setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24);
        if (diffDays < 0) return 'expired';
        if (diffDays <= 3) return 'warning';
        return 'normal';
    };

    const handleAddGoal = async () => {
        const trimmedGoal = goalInput.trim();
        if (!trimmedGoal) {
            setError('Goal cannot be empty.');
            return;
        }
        if (currentGoals.some(g => g.target === trimmedGoal)) {
            setError('You have already added this goal.');
            return;
        }

        const newGoal = { target: trimmedGoal, deadline: showDeadline && goalDeadline.trim() ? goalDeadline : null };
        const updatedGoals = [...currentGoals, newGoal];

        setIsSaving(true); setError('');
        try {
            if (userId) await dbUpdateGoals(userId, updatedGoals.map(g => ({ target: g.target, deadline: g.deadline })));
            setCurrentGoals(updatedGoals);
            if (onGoalsUpdate) onGoalsUpdate(updatedGoals);
            setGoalInput(''); setGoalDeadline(''); setShowDeadline(false);
        } catch (err) {
            console.error("Error saving goal:", err);
            setError("Failed to save goal. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveGoal = async (goalText) => {
        const updatedGoals = currentGoals.filter(g => g.target !== goalText);
        setIsSaving(true); setError('');
        try {
            if (userId) await dbUpdateGoals(userId, updatedGoals.map(g => ({ target: g.target, deadline: g.deadline })));
            setCurrentGoals(updatedGoals);
            if (onGoalsUpdate) onGoalsUpdate(updatedGoals);
        } catch (err) {
            console.error("Error removing goal:", err);
            setError("Failed to remove goal. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddRestriction = async () => {
        const trimmed = restrictionInput.trim();
        if (!trimmed) {
            setError('Restriction cannot be empty.');
            return;
        }
        if (currentRestrictions.includes(trimmed)) {
            setError('You have already added this restriction.');
            return;
        }

        const updatedRestrictions = [...currentRestrictions, trimmed];
        setIsSaving(true); setError('');
        try {
            if (userId) await dbUpdateRestrictions(userId, updatedRestrictions);
            setCurrentRestrictions(updatedRestrictions);
            if (onRestrictionsUpdate) onRestrictionsUpdate(updatedRestrictions);
            setRestrictionInput('');
        } catch (err) {
            console.error("Error saving restriction:", err);
            setError("Failed to save restriction. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveRestriction = async (r) => {
        const updatedRestrictions = currentRestrictions.filter(item => item !== r);
        setIsSaving(true); setError('');
        try {
            if (userId) await dbUpdateRestrictions(userId, updatedRestrictions);
            setCurrentRestrictions(updatedRestrictions);
            if (onRestrictionsUpdate) onRestrictionsUpdate(updatedRestrictions);
        } catch (err) {
            console.error("Error removing restriction:", err);
            setError("Failed to remove restriction. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered size="lg" backdrop="static"> {/* backdrop static to prevent closing on saving */}

            <Modal.Header closeButton={!isSaving}>
                <Modal.Title>User Dashboard</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
                {isSaving && <div className="text-center my-2"><Spinner animation="border" size="sm" /> Saving...</div>}

                <h6 className="mb-3">📈 Scan Activity</h6>
                <div className="mb-4" style={{ position: 'relative', height: '250px' }}>
                    <canvas ref={chartRef}></canvas>
                </div>
                <hr />
                <h6>🎯 Your Goals</h6>
                <Form className="mb-3">
                    <div className="d-flex flex-column flex-md-row align-items-md-end gap-2">
                        <Form.Control type="text" placeholder="E.g. Lose 5kg" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} disabled={isSaving} />
                        <div className="d-flex flex-column">
                            <Form.Check type="checkbox" label="Add deadline" checked={showDeadline} onChange={() => setShowDeadline(prev => !prev)} disabled={isSaving} />
                            <div className={`fade-toggle ${showDeadline ? 'show' : ''}`}>
                                <Form.Label className="mb-1 text-muted" style={{ fontSize: '0.8rem' }}>Deadline (Optional)</Form.Label>
                                <Form.Control type="date" value={goalDeadline} onChange={(e) => setGoalDeadline(e.target.value)} disabled={isSaving} />
                            </div>
                        </div>
                        <Button onClick={handleAddGoal} disabled={!goalInput.trim() || isSaving}>{isSaving ? 'Adding...' : 'Add'}</Button>
                    </div>
                </Form>
                <div className="mb-3 d-flex flex-wrap gap-2">
                    {(currentGoals || []).length > 0 ? (currentGoals || []).map((g, idx) => {
                        const status = getGoalStatus(g?.deadline);
                        let bg = 'primary'; let style = {}; let icon = '🎯 ';
                        if (status === 'expired') { bg = 'secondary'; style = { opacity: 0.7 }; icon = '❌ '; }
                        else if (status === 'warning') { bg = 'warning'; icon = '⚠️ '; }
                        return (
                            <div className="badge-container" key={`goal-${idx}`}>
                                <Badge bg={bg} text={bg === 'warning' ? 'dark' : 'white'} style={style} pill className="p-2">
                                    {icon}{g.target}
                                    {g.deadline && (<small className="ms-1 opacity-75">(by {new Date(g.deadline).toLocaleDateString()})</small>)}
                                </Badge>
                                {!isSaving && <div className="badge-remove" onClick={() => handleRemoveGoal(g.target)}>×</div>}
                            </div>
                        );
                    }) : <p className="text-muted fst-italic">No goals added yet.</p>}
                </div>
                <hr />
                <h6>🚫 Restrictions</h6>
                <Form className="mb-3">
                    <div className="d-flex gap-2">
                        <Form.Control type="text" placeholder="E.g. Gluten-free, Vegan" value={restrictionInput} onChange={(e) => setRestrictionInput(e.target.value)} disabled={isSaving} />
                        <Button onClick={handleAddRestriction} disabled={!restrictionInput.trim() || isSaving}>{isSaving ? 'Adding...' : 'Add'}</Button>
                    </div>
                </Form>
                <div className="d-flex flex-wrap gap-2">
                    {(currentRestrictions || []).length > 0 ? (currentRestrictions || []).map((r, idx) => (
                        <div className="badge-container" key={`restriction-${idx}`}>
                            <Badge bg="danger" pill className="p-2">{r}</Badge>
                            {!isSaving && <div className="badge-remove" onClick={() => handleRemoveRestriction(r)}>×</div>}
                        </div>
                    )) : <p className="text-muted fst-italic">No restrictions added yet.</p>}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isSaving}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UserDashboardModal;